import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";

import { API_URL } from "../config/api";
import RegistrationTracking from "../components/RegistrationTracking";
import AdminGate from "../components/AdminGate";
import { adminFetch } from "../services/adminAuth";

type ActivityEvent = {
  at: string;
  origin: "front" | "server";
  type: string;
  visitorId?: string;
  registrationCode?: string;
  path?: string;
  details?: Record<string, unknown>;
};

type CodeReport = {
  registrationCode: string;
  createdAt: string;
  lastUsedAt: string;
  lastActivityAt: string | null;
  visitors: number;
  summary: {
    qr: boolean;
    licence?: boolean;
    manual: boolean;
    import: boolean;
    submitted: number;
    rejected: number;
    judokas: number;
  };
  events: ActivityEvent[];
};

type ActivityReport = {
  licenceReferenceDate: string;
  anonymousVisitors: number;
  hiddenCodes?: number;
  codes: CodeReport[];
};

const PAGE_NAMES: Record<string, string> = {
  "/": "Accueil",
  "/agenda": "Agenda",
  "/contact": "Contact",
  "/registration": "Inscriptions",
  "/manage-registration": "Mes inscriptions",
};

const SOURCE_LABELS: Record<string, string> = {
  qr: "par QR code",
  licence: "par n° de licence",
  manual: "saisie manuelle",
  import: "import Excel",
};

const LICENCE_LABELS: Record<string, string> = {
  valid: "licence valide",
  invalid: "licence non valable",
  unknown: "licence non vérifiée",
};

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString("fr-BE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

function describe(e: ActivityEvent): { step: string; detail?: string } {
  const d = e.details ?? {};

  switch (e.type) {
    case "page_view":
      return {
        step: "Page visitée",
        detail: PAGE_NAMES[e.path ?? ""] ?? e.path,
      };
    case "code_generated":
      return { step: "Code d'inscription généré" };
    case "code_entered":
      return { step: "Code existant saisi" };
    case "add_opened":
      return {
        step:
          d.mode === "qr"
            ? "Formulaire ouvert, pré-rempli par QR code"
            : d.mode === "licence"
              ? "Formulaire ouvert, pré-rempli par n° de licence"
              : "Formulaire « Ajouter un judoka » ouvert (étranger / manuel)",
      };
    case "add_cancelled":
      return { step: "Formulaire abandonné sans inscription" };
    case "qr_scan_started":
      return { step: "Scan QR démarré" };
    case "qr_scan_success":
      return { step: "Licence lue via QR code" };
    case "qr_scan_failed":
      return {
        step: "Échec du scan",
        detail:
          d.error === "camera" ? "Caméra inaccessible" : String(d.error ?? ""),
      };
    case "licence_lookup_opened":
      return { step: "Recherche par n° de licence ouverte" };
    case "licence_lookup_success":
      return { step: "Licence trouvée par n° de licence" };
    case "licence_lookup_failed":
      return {
        step: "Échec de la recherche par n° de licence",
        detail: String(d.error ?? ""),
      };
    case "import_opened":
      return { step: "Import Excel ouvert" };
    case "import_previewed":
      return {
        step: "Import vérifié (prévisualisation)",
        detail: `${d.valid} valide(s), ${d.invalid} invalide(s)`,
      };
    case "import_cancelled":
      return {
        step: "Import abandonné",
        detail: d.previewed ? "après prévisualisation" : undefined,
      };
    case "judoka_added":
      return {
        step: "Judoka inscrit",
        detail: [
          SOURCE_LABELS[String(d.source)] ?? "",
          LICENCE_LABELS[String(d.licenceStatus)] ?? "",
        ]
          .filter(Boolean)
          .join(", "),
      };
    case "judoka_rejected":
      return {
        step: "Inscription refusée par le serveur",
        detail: Array.isArray(d.errors) ? d.errors.join(", ") : undefined,
      };
    case "judokas_imported":
      return { step: "Import validé", detail: `${d.count} judoka(s)` };
    case "import_rejected":
      return {
        step: "Import refusé par le serveur",
        detail: `${d.invalid} ligne(s) invalide(s)`,
      };
    default:
      return { step: e.type };
  }
}

function CodeAccordion({ report }: { report: CodeReport }) {
  const { summary, events } = report;
  const showVisitor = report.visitors > 1;

  return (
    <Accordion
      disableGutters
      slotProps={{ transition: { unmountOnExit: true } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          spacing={1.5}
          sx={{ width: "100%" }}
        >
          <Typography
            fontFamily="monospace"
            fontWeight="bold"
            title={report.registrationCode}
          >
            {report.registrationCode.slice(0, 8)}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            créé le {formatDateTime(report.createdAt)}
            {report.lastActivityAt &&
              ` · dernière activité le ${formatDateTime(report.lastActivityAt)}`}
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          {summary.qr && <Chip size="small" label="QR code" />}
          {summary.licence && <Chip size="small" label="N° de licence" />}
          {summary.manual && <Chip size="small" label="Saisie manuelle" />}
          {summary.import && <Chip size="small" label="Import" />}
          {summary.rejected > 0 && (
            <Chip
              size="small"
              color="warning"
              label={`${summary.rejected} refus`}
            />
          )}
          <Chip
            size="small"
            color={summary.judokas > 0 ? "success" : "default"}
            label={`${summary.judokas} inscrit(s)`}
          />
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        <Typography variant="h6" gutterBottom>
          Parcours
        </Typography>

        {events.length === 0 ? (
          <Alert severity="info" sx={{ mb: 3 }}>
            Aucune activité enregistrée : ce code a été créé avant la
            mise en place du suivi des connexions.
          </Alert>
        ) : (
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  {showVisitor && <TableCell>Appareil</TableCell>}
                  <TableCell>Étape</TableCell>
                  <TableCell>Détail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((e, index) => {
                  const { step, detail } = describe(e);

                  return (
                    <TableRow key={`${e.at}-${index}`}>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatDateTime(e.at)}
                      </TableCell>
                      {showVisitor && (
                        <TableCell sx={{ fontFamily: "monospace" }}>
                          {e.visitorId?.slice(0, 4) ?? "serveur"}
                        </TableCell>
                      )}
                      <TableCell>{step}</TableCell>
                      <TableCell>{detail}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Typography variant="h6" gutterBottom>
          Judokas inscrits
        </Typography>

        <RegistrationTracking registrationCode={report.registrationCode} />
      </AccordionDetails>
    </Accordion>
  );
}

export default function JayConnexions() {
  return (
    <AdminGate role="admin">
      <ConnexionsContent />
    </AdminGate>
  );
}

function ConnexionsContent() {
  const [report, setReport] = useState<ActivityReport | null>(null);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    adminFetch(`${API_URL}/activity`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        setReport(data);
        setError(false);
      })
      .catch(() => setError(true));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", px: 3, py: 4 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="h4" fontWeight="bold">
          Connexions
        </Typography>

        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} onClick={load}>
            Actualiser
          </Button>
          <Button
            component={Link}
            to="/jay"
            variant="outlined"
            startIcon={<ArrowBackIcon />}
          >
            Retour au suivi
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error">
          Impossible de récupérer le journal des connexions. Le service
          tourne peut-être avec une version antérieure à la v1.1.2.
        </Alert>
      )}

      {report && (
        <>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {report.codes.length} code(s) d'inscription
            {report.hiddenCodes
              ? ` (+ ${report.hiddenCodes} ancien(s) code(s) de test masqué(s))`
              : ""}{" "}
            · {report.anonymousVisitors} visiteur(s) sans code · validité
            des licences vérifiée au{" "}
            {new Date(report.licenceReferenceDate).toLocaleDateString("fr-BE")}
          </Typography>

          <Stack spacing={1}>
            {report.codes.map((code) => (
              <CodeAccordion key={code.registrationCode} report={code} />
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
}
